/**
 * `trackLegacyEvent()` — the v1 (Segment / PostHog / Datadog) sender as a plain
 * function, outside the Rematch model.
 *
 * This is the body of `analyticsScribe.trackEvent` moved verbatim: same props,
 * same order, same three destinations, same `$groups` / `pageUrl` /
 * `installer_type` handling. The model effect is now a one-line delegate, so
 * every existing dispatch caller is unaffected and the two calling conventions
 * cannot drift.
 *
 * It exists so that a call site can emit the v1 pair of a legacy event without
 * holding a Rematch `dispatch` — the model wrappers that composed `trackEvent`
 * + `trackEventV2` become plain functions (see `./commerce/checkout-events`).
 *
 * NEW EVENTS DO NOT BELONG HERE. The v1 path is deprecated; new events call
 * `track()` from `./track` (see `.ai-context/analytics.md`).
 */
import {
  type OrganizationTemplateChangedBy,
  type OrganizationTemplateUsedFor,
} from '@/container/organization/type';
import { type TabType } from '@/hooks/query/useQuerySessionWindows';
import {
  getAppType,
  getInstallSourceAnalyticsTraits,
  getPlatform,
  getScreenDimensions,
  segmentClient,
} from '@/lib/analytics/legacy-sender-context';
import { type TaskEdit } from '@/lib/analytics/scribe/task';
import { type DesktopCaptureOriginProp } from '@/models/analytics/features/errors';
import {
  type EvidenceLocation,
  type EvidenceShareSocialPlatform,
  type EvidenceSourceSelectionMode,
} from '@/models/analytics/features/evidence';
import { type SegmentObjectName } from '@/models/analytics/segment-names';
import {
  type CreateEHRProfileRequest,
  type FeedbackTags,
  type IntegrationRequest,
  type WordLibrary,
} from '@/types';
import { type AudioGraphStore } from '@heidi/capture';
import { type Period } from '@heidi/commerce/contracts';
import {
  type Brain,
  type CSATRating,
  type DateFormat,
  type EHRProvider,
  type GenerationMethod,
  type LanguageCode,
  type OrganisationRole,
  type OrganisationSize,
  type SpellingPreference,
  type SubscriptionTier,
  type TemplateCategory,
  type TemplateVisibility,
  type VoiceStyle,
} from '@heidi/schema/ml-scribe';
import {
  type CustomStyle,
  type SessionResponse,
  type SessionUpdate,
} from '@heidi/session/contracts';
import { type ClinicalTaskCategory } from '@heidi/tasks/contracts';
import { type UpdateUserRequest } from '@heidi/workspace/contracts';
// `from_integration` is enrichment the v1 payload has always carried, and the
// integration atoms are the only source for it. Read through the getter rather
// than a hook: this is a plain function with no React context, and moving the
// read to the caller would put it at every call site instead of one.
import { getIntegrationEhrProvider } from '@/atoms/integrationAtoms';
import { getDesktopInstallerType } from '@/utils/appConfiguration';
import { datadogRum } from '@datadog/browser-rum';
import { getAnalyticsContext } from '@heidi/runtime/analytics';
import posthog from 'posthog-js';
import { browserName, deviceType, osName } from 'react-device-detect';

// Read through the capture door rather than the AudioLevelMonitor compatibility path.
type AudioStatus = AudioGraphStore['audioStatus'];

enum SegmentContext {
  // paywall context
  PRO_ACTION_ON_FREE = 'pro_action_on_free',
  FREE_USER_EXTEND_PRO_TRIAL = 'free_user_extend_pro_trial',
  INTEGRATION = 'integration',
  TEMPLATE_VISIBILITY_TO_TEAM = 'template_visibility_to_team',
  ASK_AI_INPUT = 'ask_ai_input',
  NOTE_GEN_QUOTA_EXCEEDED = 'note_gen_quota_exceeded',
  ACCOUNT_PROFILE_TO_PRO = 'account_profile_to_pro',
  ACCOUNT_PROFILE_TO_PRACTICE = 'account_profile_to_practice',
  OPEN_ON_SESSION_HEADER = 'session_header',
  OPEN_ON_SIDEBAR = 'sidebar',
  OEPN_ON_NO_TEAM_MEMBER_PAGE = 'no_team_member_page',
}

export type TrackingProps = {
  action: string;
  eventName?: string;
  sessionId?: string;
  documentId?: string;
  microphone?: string;
  userDetail?: string;
  aiCommandText?: string;
  aiCommandId?: string;
  presetCommandId?: string;
  existingTemplate?: string;
  specialtyId?: string;
  specialtyName?: string;
  otherSpecialty?: string;
  settingsUpdatedData?: UpdateUserRequest;
  templateId?: string;
  templateType?: TemplateVisibility;
  templateName?: string;
  templateContent?: string;
  templatePinned?: boolean;
  templateFavourited?: boolean;
  questionAskedLength?: number;
  paymentPeriod?: Period;
  language?: LanguageCode;
  multipleLanguages?: LanguageCode[];
  fileSize?: number;
  error?: string;
  errorDescription?: string;
  generationMethod?: GenerationMethod;
  voiceStyle?: VoiceStyle;
  oldVoiceStyle?: VoiceStyle;
  sentDocumentEmail?: string;
  createdTimeLocal?: string;
  integrationRequest?: IntegrationRequest;
  transcribeMode?: SessionResponse['transcribe_mode'];
  duration?: number;
  outputLanguage?: SessionUpdate['language_code'];
  searchQuery?: string;
  filter?: string;
  sort?: string;
  // for demo tour
  device?: 'DESKTOP' | 'MOBILE';
  selectedSoftware?: EHRProvider;
  siteId?: string;
  guId?: string;
  firstName?: string;
  lastName?: string;
  userId?: string;
  userInfo?: CreateEHRProfileRequest;
  date?: Date;
  teamName?: string;
  teamId?: string;
  invitedEmail?: string;
  invitationId?: string;
  memberId?: string;
  joinTeamMethod?: string;
  teamLink?: string;
  memberEmail?: string;
  referralId?: string;
  couponId?: string;
  organisationSize?: OrganisationSize;
  organisationRole?: OrganisationRole;
  organisationName?: string;
  ehrProvider?: string;
  withSdes?: boolean;
  heidiSectionTitle?: string;
  ehrSectionTitle?: string;
  isUnlinking?: boolean;
  custom_utm_source?: string | null;
  deeplink_utm_source?: string | null;
  deeplink_utm_medium?: string | null;
  deeplink_utm_campaign?: string | null;
  baa_required?: boolean;
  compliance_kit_required?: boolean;
  phone_number_provided?: boolean;
  reason?: string;
  // sign in
  sign_in_method?: 'email' | 'google' | 'microsoft' | 'apple';
  // chat
  mode?: string;
  previous_mode?: string;
  new_mode?: string;
  query?: string;
  message_length?: number;
  // feedback
  details?: string;
  documentName?: string;
  rating?: CSATRating;
  noteCategory?: TemplateCategory | TabType;
  noteId?: string;
  feedbackTags?: FeedbackTags;
  feedbackDetail?: string;
  timestamp?: string;
  feedbackCategory?: TabType;
  // paywall
  target_plan?: SubscriptionTier;
  paywall_context?: SegmentContext | null;
  enabled?: boolean;
  adminId?: string;
  hasCustomText?: boolean;
  // global config
  dateFormat?: DateFormat;
  spellingPreference?: SpellingPreference;
  wordLibrary?: WordLibrary;

  isRecordingV2Enabled?: boolean;
  isTabSharing?: boolean;
  transcriptionServiceVersion?: 'v1' | 'v2';

  // template community
  search?: string;
  templateVisibility?: TemplateVisibility;
  formFilling?: boolean;

  // form filling document generation
  requestTime?: string;
  responseTime?: string;
  status?: 'success' | 'failed';
  failedReason?: string;

  // context tab
  wordCount?: number;

  // template chat builder
  creationType?: 'chat' | 'existing_template' | 'upload';
  numberOfUploads?: number;
  hasSuggestions?: boolean;
  editType?: 'inline' | 'chat' | 'suggestion';
  hasDictation?: boolean;
  confirmed?: boolean;

  // export header/footer settings
  has_logo?: boolean;
  has_text?: boolean;
  signature_type?: 'image' | 'text' | null;
  position?: 'left' | 'center' | 'right';

  // snippets
  snippetId?: string;
  contentLength?: number;
  shorthandLength?: number;
  from?: string;
  snippets?: {
    shorthand: string;
    content: string;
    verbal_trigger?: string;
  }[];
  snippetCount?: number;

  // replacements
  replacementId?: string;
  findLength?: number;
  replaceLength?: number;
  count?: number;

  // shortcuts
  shortcutName?: string;

  // link sessions to patient profile
  sessionIds?: string[];
  patientId?: string;
  numberOfSessions?: number;
  isFromEHR?: boolean;

  // integrations
  profile_name?: string;

  // notifications
  isInmailNotificationEnabled?: boolean;
  isSessionExpiringNotificationEnabled?: boolean;
  isNotificationPreferencesSavedFromSettingsPage?: boolean;
  isNotificationPreferencesSavedFromModal?: boolean;

  // speaker size — the one dispatcher reads them off `inspect_*_file()`, which
  // returns bytes, so both have always arrived as numbers.
  speakerSize?: number | string;
  audioSize?: number | string;
  version?: string;

  step?: string;

  size?: number | string;
  numberFilesDeleted?: number;

  // streaming
  noteGenerationStartTime?: number;
  noteGenerationEndTime?: number;

  totalDuration?: number;
  durationBeforeWaitingUpload?: number;
  durationWaitingToUpload?: number;
  durationWaitingToStream?: number;
  durationStreaming?: number;
  durationCompletingStreaming?: number;

  index?: number;

  location?:
    | 'cta-button'
    | 'account-form'
    | 'quota-reminder'
    | 'banner'
    | 'notification'
    | 'settings'
    | 'end-trial-modal'
    | 'one-day-reminder';
  period?: Period;
  selectHours?: number;
  isSuccess?: boolean;

  recordingId?: string | number;
  chunkId?: string | number;
  chunkCount?: number;
  currentSessionId?: string;
  newSessionId?: string;

  // customise template menu
  customStyle?: CustomStyle;
  oldCustomStyle?: CustomStyle;
  transitionType?: string;
  brain?: Brain;
  voice?: VoiceStyle;
  quotaExceeded?: boolean;
  inputDevice?: string;
  inputDeviceId?: string;

  // task related
  taskId?: string;
  taskTimeToCompletion?: number;
  taskCategory?: ClinicalTaskCategory;
  taskSource?: string;
  taskEdits?: TaskEdit[];
  audioLevel?: AudioStatus;

  // coding sidebar
  codesetsEnabled?: string[];
  totalCodesAvailable?: number;
  kindeUserId?: string;

  //pyne
  region?: string;

  // phone number tracking
  country?: string;
  phoneNumber?: string;

  // citation tracking
  citationUrl?: string;
  citationTitle?: string;
  citationSource?: 'response' | 'sources';
  // global template
  previousVisibility?: TemplateVisibility;
  visibility?: TemplateVisibility;
  sharedTeams?: string[];
  changedBy?: OrganizationTemplateChangedBy;
  usedFor?: OrganizationTemplateUsedFor;
  // feedback tracking
  feedbackType?: 'positive' | 'negative';

  // template tracking
  is_pinned_template?: boolean;
  is_starter_pack_template?: boolean;

  // share-via-email merged modal (release-scribe-send-as-email-merged-modal)
  recipient_count?: number;
  included_link?: boolean;
  link_scope?: string;
  link_ttl?: string;
  partial_failure?: boolean;

  // evidence tracking
  evidence_location?: EvidenceLocation;
  is_evidence_pro?: boolean;
  is_team_member?: boolean;
  query_length?: number;
  sources_count?: number;
  sources?: string[];
  selection_mode?: EvidenceSourceSelectionMode;
  preset_primary_evidence?: boolean;
  preset_local_guidelines?: boolean;
  preset_web_search?: boolean;
  chat_id?: string;
  session_id?: string;
  duration_ms?: number;
  time_to_first_token_ms?: number;
  references_count?: number;
  error_message?: string;
  error_code?: string;
  // desktop IPC error event (trackTauriErrorEvent)
  capture_origin?: DesktopCaptureOriginProp;
  preset_type?: 'primary_evidence' | 'local_guidelines' | 'web_search';
  is_enabled?: boolean;
  custom_sources_count?: number;
  click_source?: 'sidebar' | 'header' | 'history_overlay';
  subscription_period?: 'monthly' | 'yearly';
  subscription_tier?: string;
  language_code?: string;
  specialty_id?: string;

  // export header/footer tracking
  has_custom_header?: boolean;
  has_custom_footer?: boolean;
  has_signature?: boolean;
  // evidence onboarding
  has_existing_specialty?: boolean;
  has_existing_jurisdiction?: boolean;
  jurisdiction?: string;
  jurisdiction_code?: string;
  specialty_changed?: boolean;
  jurisdiction_changed?: boolean;

  // share evidence chat
  platform?: EvidenceShareSocialPlatform;
  share_token?: string;
  is_authenticated?: boolean;

  // integration user searched
  integration_type?: string;
  guid?: string;
  first_name?: string;
  last_name?: string;
};

export function trackLegacyEvent<T extends TrackingProps>(input: {
  name: SegmentObjectName;
  props: T;
  /** Optional URL to override page URL in analytics (useful for preserving attribution params) */
  pageUrl?: string | null;
}): void {
  const { name, props, pageUrl } = input;
  const currentTeamId = getAnalyticsContext().teamId;

  const baseProps: Record<string, unknown> = {
    deviceType,
    osName,
    browserName,
    ...getScreenDimensions(),
    eventName: props.eventName || `${name} ${props.action}`,
    from_integration: getIntegrationEhrProvider(),
    App: getAppType(),
    app_type: getAppType(),
    client_platform: getAppType(),
    platform: getPlatform(),
    ...props,
    // Placed after the props spread so the system value always wins
    // over any accidental `installer_type` key in caller-supplied props.
    installer_type: getDesktopInstallerType(),
    ...getInstallSourceAnalyticsTraits(),
  };

  const segmentProps = {
    ...baseProps,
    ...(currentTeamId && {
      $groups: {
        segment_group: currentTeamId,
      },
    }),
  };

  // Include attributed URL in PostHog props if provided
  const posthogProps = pageUrl
    ? { ...baseProps, $current_url: pageUrl, attributed_url: pageUrl }
    : baseProps;
  posthog.capture(`${name}_${props.action}`, posthogProps);

  datadogRum.addAction(`${name}_${props.action}`, {
    ...baseProps,
    teamId: currentTeamId,
    timestamp: new Date().toISOString(),
    source: 'rum_action',
    // Include attributed URL if provided
    ...(pageUrl && { attributed_url: pageUrl }),
  });

  // Pass custom context to override page URL if provided (preserves attribution params after OAuth redirects)
  const segmentOptions = pageUrl ? { context: { page: { url: pageUrl } } } : undefined;

  void segmentClient.track(`${name}_${props.action}`, segmentProps, segmentOptions);
}
